import React, { useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { backend_url, currency } from '../../App'
import { ShopContext } from '../../Context/ShopContext'

// Static rating based on id for consistent display
const getRating = (id) => {
  const ratings = [4.5, 4.8, 4.3, 5.0, 4.7, 4.2, 4.9, 4.6];
  return ratings[(id || 0) % ratings.length];
};
const getReviewCount = (id) => {
  const counts = [128, 243, 89, 312, 56, 187, 421, 74];
  return counts[(id || 0) % counts.length];
};

const StarRating = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="11" height="11" viewBox="0 0 24 24"
          fill={i <= full ? '#fbbf24' : (i === full + 1 && half ? 'url(#half)' : '#2a3a52')}
          stroke="none">
          <defs>
            <linearGradient id="half" x1="0" x2="100%" y1="0" y2="0">
              <stop offset="50%" stopColor="#fbbf24"/>
              <stop offset="50%" stopColor="#2a3a52"/>
            </linearGradient>
          </defs>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </div>
  );
};

const Item = (props) => {
  const { addToCart } = useContext(ShopContext);
  const navigate = useNavigate();

  const discount = props.old_price > props.new_price
    ? Math.round(((props.old_price - props.new_price) / props.old_price) * 100)
    : 0;
  const savings = props.old_price > props.new_price ? props.old_price - props.new_price : 0;
  const rating = getRating(props.id);
  const reviews = getReviewCount(props.id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(props.id);
  };

  const handleView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.scrollTo(0, 0);
    navigate(`/product/${props.id}`);
  };

  return (
    <div className='group flex flex-col h-full bg-gradient-to-br from-[#0f1829] via-[#0d1526] to-[#0a1020] border border-white/[0.08] rounded-[18px] overflow-hidden transition-all duration-400 hover:-translate-y-4 hover:border-[rgba(0,212,255,0.6)] hover:shadow-[0_0_30px_rgba(0,212,255,0.2),0_30px_80px_rgba(0,212,255,0.12),0_50px_120px_rgba(0,0,0,0.8)] cursor-pointer backdrop-blur-sm'>

      {/* Image area */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#101b2e] to-[#0a1020]" style={{ aspectRatio: '1/1' }}>

        {/* Discount badge - Enhanced */}
        {discount > 0 && (
          <div className="absolute top-4 left-4 z-20 flex flex-col items-center justify-center w-[55px] h-[55px] rounded-[14px] bg-gradient-to-br from-red-500 via-red-600 to-rose-700 shadow-xl shadow-red-900/60 backdrop-blur-sm border border-red-400/30 group-hover:scale-110 transition-transform duration-300">
            <span className="text-white text-[13px] font-black leading-none">-{discount}%</span>
            <span className="text-white/95 text-[9px] font-bold">SAVE</span>
          </div>
        )}

        {/* Action buttons - Enhanced */}
        <div className="absolute top-4 right-4 z-30 flex flex-col gap-3">
          <button
            onClick={handleView}
            className="w-11 h-11 rounded-[14px] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 cursor-pointer border border-cyan-400/40 backdrop-blur-md hover:scale-125 hover:shadow-2xl active:scale-95"
            style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.12), rgba(0,212,255,0.05))', boxShadow: '0 6px 16px rgba(0,212,255,0.25)' }}
            title="Quick View"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2.5">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button
            onClick={handleAddToCart}
            className="w-11 h-11 rounded-[14px] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 delay-100 cursor-pointer border-none hover:scale-125 hover:shadow-2xl active:scale-95"
            style={{ background: 'linear-gradient(135deg, #00d4ff 0%, #0095c8 100%)', boxShadow: '0 6px 20px rgba(0,212,255,0.6)' }}
            title="Add to Cart"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          </button>
        </div>

        {/* Hover overlay - Enhanced */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#060c18]/80 via-[#060c18]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 pointer-events-none" />

        {/* Product image */}
        <Link to={`/product/${props.id}`} onClick={() => window.scrollTo(0, 0)}>
          <img
            src={String(props.image).startsWith('http') ? props.image : backend_url + props.image}
            alt={props.name}
            className="w-full h-full object-cover block transition-transform duration-600 group-hover:scale-[1.12]"
          />
        </Link>

        {/* Premium shimmer sweep on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity duration-300"
          style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(0,212,255,0.15) 50%, transparent 70%)' }} />
        
        {/* Corner accent on hover */}
        <div className="absolute top-0 right-0 w-24 h-24 opacity-0 group-hover:opacity-60 transition-opacity duration-300 pointer-events-none z-5"
          style={{ background: 'radial-gradient(circle at top right, rgba(0,212,255,0.3), transparent 70%)' }} />
      </div>

      {/* Info area - Enhanced */}
      <div className="flex flex-col flex-1 p-5 gap-3.5 bg-gradient-to-b from-white/[0.03] via-transparent to-[rgba(0,212,255,0.01)]">
        {/* Name */}
        <p className="text-[#d1dce8] text-[14px] font-semibold leading-[1.5] line-clamp-2 min-h-[38px] group-hover:text-[#00d4ff] transition-colors duration-200">{props.name}</p>

        {/* Rating + Reviews */}
        <div className="flex items-center justify-between gap-2 py-2.5 px-3 rounded-[11px] bg-gradient-to-r from-white/[0.05] to-white/[0.02] border border-white/[0.07] group-hover:border-white/[0.12] transition-all duration-200">
          <div className="flex items-center gap-1.5">
            <StarRating rating={rating} />
            <span className="text-[#fbbf24] text-[11.5px] font-bold">{rating}</span>
          </div>
          <span className="text-[#4a5a6f] text-[10px] bg-white/[0.05] px-2.5 py-1 rounded-full">{reviews}</span>
        </div>

        {/* Price section - Premium */}
        <div className="flex items-end gap-3 mt-auto pt-2">
          <div className="flex-1">
            <span className="text-[#6b7a8d] text-[10px] block mb-1.5 font-semibold">Price</span>
            <div className="flex items-baseline gap-2.5">
              <span className="text-[#00d4ff] text-[20px] font-black leading-none drop-shadow-[0_0_12px_rgba(0,212,255,0.4)]">{currency}{props.new_price?.toLocaleString()}</span>
              {props.old_price > props.new_price && (
                <span className="text-[#3d4f66] text-[11px] font-semibold line-through">{currency}{props.old_price?.toLocaleString()}</span>
              )}
            </div>
          </div>
        </div>

        {/* Savings badge - Premium */}
        {savings > 0 && (
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-[11px] bg-gradient-to-r from-emerald-500/[0.18] via-green-500/[0.12] to-emerald-600/[0.1] border border-emerald-400/[0.35] backdrop-blur-sm group-hover:from-emerald-500/[0.25] transition-all duration-200">
            <span className="text-lg">💚</span>
            <span className="text-emerald-300 text-[11.5px] font-bold">Save {currency}{savings.toLocaleString()}</span>
          </div>
        )}

        {/* Add to cart button — full width */}
        <button
          onClick={handleAddToCart}
          className="w-full h-11 rounded-[12px] border-none text-[#060c18] text-[12.5px] font-extrabold cursor-pointer font-[inherit] tracking-wide translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 mt-3 bg-gradient-to-r from-[#00d4ff] via-[#00a8e8] to-[#0095c8] hover:shadow-2xl active:scale-95"
          style={{ boxShadow: '0 6px 20px rgba(0,212,255,0.45)' }}
        >
          ✨ Add to Cart
        </button>
      </div>
    </div>
  )
}

export default Item
